/* Copyright (c) 2020-2023, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Observable, Subject, ReplaySubject, of, defer, PartialObserver, EMPTY } from 'rxjs';
import { concatMap, mergeMap, catchError, finalize } from 'rxjs/operators';
import { isEqual, orderBy } from 'lodash';

export type GenericObservableFunction<T> = (...args: any[]) => Observable<T>;

export interface SerialiazableRequest<T> {
    readonly workerFn: Observable<T>;
    readonly response$: Subject<T>;
}

/**
 * Useful when a process always requires requests to be sent in a serialized
 * fashion, one after another, with some work done after each request.
 *
 * Usage
 * Create a new instance of the class, passing options as described below.
 * Add Observable work to the queue using queue(). This frees up callers to
 * call queue() any number of times on the instance, but each request will be
 * processed in the order that it was queued. You also have the ability to
 * to additional async work before marking the request complete. See param
 * descriptions in constructor for details.
 */
export class RequestSerializer<T> {
    private readonly requestQueue$: Subject<SerialiazableRequest<T>>;
    private onRequestComplete$: ReplaySubject<boolean>;

    constructor(
        /**
         * Set:   Allows you to control when each request should be considered
         *        completed based on some async work, before moving on to the
         *        next item.
         * Unset: Tracks each individual request's completion using the observable
         *        itself.
         */
        private completesExternally?: boolean,
        /**
         * Optional set of callbacks to be executed at next, error and complete
         * stages of each request.
         */
        private callbacks?: PartialObserver<T>,
        /**
         * If the workerFn can be defined when calling new (), then no need to pass
         * it in every time. Just params can be passed if the staticWorkerFn is set.
         */
        private staticWorkerFn?: GenericObservableFunction<T>
    ) {
        this.requestQueue$ = new Subject();
        this.startRequestQueue();
    }

    private startRequestQueue(): void {
        this.requestQueue$
            .pipe(
                concatMap((request: SerialiazableRequest<T>) =>
                    request.workerFn.pipe(
                        mergeMap(workerFnResponse => {
                            if (request.response$.observers.length) {
                                if (this.completesExternally) {
                                    this.onRequestComplete$ = new ReplaySubject(1);
                                    request.response$.next(workerFnResponse);
                                    request.response$.complete();
                                    if (this.callbacks.next) {
                                        this.callbacks.next(workerFnResponse);
                                    }
                                    return this.onRequestComplete$;
                                } else {
                                    request.response$.next(workerFnResponse);
                                    request.response$.complete();
                                    if (this.callbacks.next) {
                                        this.callbacks.next(workerFnResponse);
                                    }
                                }
                            }
                            return of(true);
                        }),
                        catchError(error => {
                            request.response$.error(error);
                            request.response$.complete();
                            if (this.callbacks.error) {
                                this.callbacks.error(error);
                            }
                            return of(error); // keep the outer stream alive
                        }),
                        finalize(() => {
                            if (this.callbacks.complete) {
                                this.callbacks.complete();
                            }
                        })
                    )
                )
            )
            .subscribe();
    }

    /**
     * Defer has been used extensively here to avoid side effects where
     * the workerFn may be updating some state (sync) and returning an
     * observable (async), but we don't want to execute the sync parts
     * before the observable is actually subscribed.
     *
     * @param args if using staticWorkerFn, just pass the params. Else
     *             pass workerFn as first argument, followed by params
     */
    queue(...args: any[]): Observable<T> {
        let workerFn: Observable<T>;
        if (this.staticWorkerFn) {
            workerFn = defer(() => this.staticWorkerFn(...args));
        } else {
            if (args.length > 1) {
                workerFn = defer(() => args[0](args.slice(1)) as Observable<T>);
            } else {
                workerFn = defer(() => args[0]() as Observable<T>);
            }
        }
        return defer(() => {
            const request: SerialiazableRequest<T> = {
                workerFn,
                response$: new Subject()
            };
            this.requestQueue$.next(request);
            return request.response$.asObservable();
        });
    }

    notifyRequestCompletion(): void {
        if (this.completesExternally) {
            this.onRequestComplete$.next(true);
            this.onRequestComplete$.complete();
        }
    }
}

export enum RequestSerializerPriority {
    High,
    Normal,
    Low
}

export interface PrioritizedSerialiazableRequest<T> extends SerialiazableRequest<T> {
    readonly args: any[];
    readonly insertionIndex: number;
    isCancelled: boolean;
    priority: RequestSerializerPriority;
}

export class PriorityRequestSerializer<T> {
    private readonly requestStream$: Subject<PrioritizedSerialiazableRequest<T>>;
    private requestQueue: PrioritizedSerialiazableRequest<T>[];
    private onRequestComplete$: ReplaySubject<boolean>;
    private isProcessing: boolean;

    constructor(
        /**
         * Set:   Allows you to control when each request should be considered
         *        completed based on some async work, before moving on to the
         *        next item.
         * Unset: Tracks each individual request's completion using the observable
         *        itself.
         */
        private completesExternally?: boolean,
        /**
         * Optional set of callbacks to be executed at next, error and complete
         * stages of each request.
         */
        private callbacks?: PartialObserver<T>
    ) {
        this.requestQueue = [];
        this.requestStream$ = new Subject();
        this.isProcessing = false;
        this.startRequestQueue();
    }

    private startRequestQueue(): void {
        this.requestStream$
            .pipe(
                concatMap((request: PrioritizedSerialiazableRequest<T>) => {
                    if (request.isCancelled) {
                        request.response$.complete();
                        return EMPTY.pipe(finalize(this.onRequestComplete.bind(this)));
                    }
                    return request.workerFn.pipe(
                        mergeMap(workerFnResponse => {
                            if (request.response$.observers.length) {
                                if (this.completesExternally) {
                                    this.onRequestComplete$ = new ReplaySubject(1);
                                    request.response$.next(workerFnResponse);
                                    request.response$.complete();
                                    if (this.callbacks.next) {
                                        this.callbacks.next(workerFnResponse);
                                    }
                                    return this.onRequestComplete$;
                                } else {
                                    request.response$.next(workerFnResponse);
                                    request.response$.complete();
                                    if (this.callbacks.next) {
                                        this.callbacks.next(workerFnResponse);
                                    }
                                }
                            }
                            return of(true);
                        }),
                        catchError(error => {
                            request.response$.error(error);
                            request.response$.complete();
                            if (this.callbacks.error) {
                                this.callbacks.error(error);
                            }
                            return of(error); // keep the outer stream alive
                        }),
                        finalize(this.onRequestComplete.bind(this))
                    );
                })
            )
            .subscribe();
    }

    private onRequestComplete() {
        if (this.callbacks.complete) {
            this.callbacks.complete();
        }
        this.isProcessing = false;
        this.processQueue();
    }

    private processQueue(): void {
        if (!this.isProcessing && this.requestQueue.length) {
            this.isProcessing = true;
            this.requestStream$.next(this.requestQueue.shift());
        }
    }

    /**
     *
     * @param priority
     * @param duplicatesAllowed Whether duplicates are allowed at the same priority level
     * @param args Pass workerFn as first argument, followed by params
     * @returns Response observable
     */
    queue(priority: RequestSerializerPriority, duplicatesAllowed: boolean, ...args: any[]): Observable<T> {
        const workerFn =
            args.length > 1
                ? defer(() => args[0](args.slice(1)) as Observable<T>)
                : defer(() => args[0]() as Observable<T>);
        return defer(() => {
            const request: PrioritizedSerialiazableRequest<T> = {
                priority,
                workerFn,
                args: [...args],
                isCancelled: false,
                response$: new Subject(),
                insertionIndex: this.requestQueue.length
            };
            if (!duplicatesAllowed) {
                const existingRequest = this.getRequest(args);
                if (existingRequest) {
                    if (priority !== existingRequest.priority) {
                        existingRequest.priority = priority;
                    }
                    return existingRequest.response$.asObservable();
                }
            }
            this.requestQueue.push(request);
            // preserves insertion order for requests at a given priority
            this.requestQueue = orderBy(this.requestQueue, ['priority', 'insertionIndex'], ['asc', 'asc']);
            this.processQueue();
            return request.response$.asObservable();
        });
    }

    private getRequest(...args: any[]): PrioritizedSerialiazableRequest<T> {
        return this.requestQueue.find(request => !request.isCancelled && isEqual([...args], request.args));
    }

    /**
     *
     * @param queryParam any query parameter used with the queue() worker function
     * @returns true,  if a request with this queryParam was found in the requestQueue
     *          false, otherwise
     */
    isQueued(queryParam: any): boolean {
        const request = this.getRequestByQueryParam(queryParam);
        return !!request;
    }

    private getRequestByQueryParam(queryParam: any): PrioritizedSerialiazableRequest<T> {
        return this.requestQueue.find(request => !request.isCancelled && request.args.includes(queryParam));
    }

    /**
     *
     * @param queryParam any query parameter used with the queue() worker function
     * @returns true,  if a request with this queryParam was found in the requestQueue
     *                 and marked for cancellation
     *          false, otherwise
     */
    cancel(queryParam: any): boolean {
        const request = this.getRequestByQueryParam(queryParam);
        if (request) {
            request.isCancelled = true;
            return true;
        }
        return false;
    }
}
